/**
 * RAM do APARELHO (não o heap do JavaScript) — proxy direto de vazamento nativo.
 *
 * <p>É a medida que o heap JS não dá. No vazamento que matava o player embedded
 * a cada ~2 h, o `VmRSS` ficou <b>plano em ~110 MB</b> enquanto a memória
 * virtual subia até o processo morrer: qualquer painel de heap teria mostrado
 * tudo verde até o app cair. Por isso a RAM do sistema, e não
 * `performance.memory`.</p>
 *
 * <p>Nenhuma plataforma expõe isso ao JavaScript — quem tem a medida é o código
 * nativo. Por isso a leitura vai pelo `IPlatformService`, que cada plataforma
 * implementa como pode: `tizen.systeminfo` na TV, `ActivityManager` no Android
 * (plugin) e no embedded (bridge do host). Browser, desktop e webOS devolvem
 * null — que é "não sei medir", nunca zero.</p>
 */

export interface MemoriaDoSistema {
  freeMb: number | null;
  totalMb: number | null;
}

const VAZIO: MemoriaDoSistema = { freeMb: null, totalMb: null };

export async function coletarMemoriaDoSistema(): Promise<MemoriaDoSistema> {
  try {
    // O PlatformService vive em `window.__effluxPlatform` (registrado pelo
    // PlatformProvider) — mesmo caminho que o health:report já usa.
    const plataforma = (
      window as unknown as {
        __effluxPlatform?: {
          getSystemMemoryMb?: () => Promise<{
            freeMb: number;
            totalMb: number | null;
          } | null>;
        };
      }
    ).__effluxPlatform;

    const info = await plataforma?.getSystemMemoryMb?.();
    if (!info) return VAZIO;
    return { freeMb: info.freeMb, totalMb: info.totalMb };
  } catch {
    // Telemetria é best-effort: roda no caminho do sync, que existe pra pôr
    // conteúdo na tela.
    return VAZIO;
  }
}

export default coletarMemoriaDoSistema;
